import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Users,
  Music2,
  DollarSign,
  Star,
  StarOff,
  Loader2,
  Shield,
  Pencil,
  Trash2,
  Upload,
  ShoppingBag,
  Activity,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminPage = () => {
  const navigate = useNavigate();
  const { user, isAdmin, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState({ recent_purchases: [], recent_users: [] });
  const [users, setUsers] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [vocab, setVocab] = useState({ content_rating: [], voice: [], language: [], moods: [], character: [] });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Dialog state
  const [editUser, setEditUser] = useState(null);
  const [editTrack, setEditTrack] = useState(null);
  const [savingUser, setSavingUser] = useState(false);
  const [savingTrack, setSavingTrack] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(null);
  const [agreements, setAgreements] = useState([]);
  const [editAgreement, setEditAgreement] = useState(null);
  const [newAgreement, setNewAgreement] = useState(null);
  const [savingAgreement, setSavingAgreement] = useState(false);

  const refreshAll = useCallback(async () => {
    const [statsRes, activityRes, usersRes, tracksRes, vocabRes, agreementsRes] = await Promise.all([
      axios.get(`${BACKEND_URL}/api/admin/stats`),
      axios.get(`${BACKEND_URL}/api/admin/recent-activity`),
      axios.get(`${BACKEND_URL}/api/admin/users`),
      axios.get(`${BACKEND_URL}/api/tracks?limit=200`),
      axios.get(`${BACKEND_URL}/api/filters/vocab`),
      axios.get(`${BACKEND_URL}/api/admin/license-agreements`),
    ]);
    setStats(statsRes.data);
    setActivity(activityRes.data);
    setUsers(usersRes.data);
    setTracks(tracksRes.data);
    setVocab(vocabRes.data);
    setAgreements(agreementsRes.data);
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!isAdmin) {
      navigate('/login');
      return;
    }
    refreshAll()
      .catch((err) => {
        console.error('Admin load error:', err);
        toast.error('Failed to load admin data');
      })
      .finally(() => setLoading(false));
  }, [isAdmin, navigate, authLoading, refreshAll]);

  // --- User actions ---
  const handleSaveUser = async (e) => {
    e.preventDefault();
    setSavingUser(true);
    try {
      const payload = { ...editUser };
      delete payload.id;
      delete payload.created_at;
      if (!payload.password) delete payload.password;
      await axios.put(`${BACKEND_URL}/api/admin/users/${editUser.id}`, payload);
      toast.success('User updated');
      setEditUser(null);
      await refreshAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user');
    } finally {
      setSavingUser(false);
    }
  };

  const handleDeleteUser = async (userId, name) => {
    if (!window.confirm(`Delete user "${name}"? This will also delete all their tracks if they are an artist.`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/users/${userId}`);
      toast.success('User deleted');
      await refreshAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  // --- Track actions ---
  const handleSaveTrack = async (e) => {
    e.preventDefault();
    setSavingTrack(true);
    try {
      const payload = { ...editTrack };
      delete payload.id;
      delete payload.artist_id;
      delete payload.artist_name;
      delete payload.audio_url;
      delete payload.created_at;
      await axios.put(`${BACKEND_URL}/api/admin/tracks/${editTrack.id}`, payload);
      toast.success('Track updated');
      setEditTrack(null);
      await refreshAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update track');
    } finally {
      setSavingTrack(false);
    }
  };

  const handleDeleteTrack = async (trackId, title) => {
    if (!window.confirm(`Delete track "${title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/tracks/${trackId}`);
      toast.success('Track deleted');
      await refreshAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete track');
    }
  };

  const handleToggleFeatured = async (track) => {
    try {
      const res = await axios.put(`${BACKEND_URL}/api/admin/tracks/${track.id}/feature`);
      setTracks((prev) => prev.map((t) => (t.id === track.id ? { ...t, is_featured: res.data.is_featured } : t)));
      toast.success(res.data.is_featured ? 'Track featured' : 'Track unfeatured');
    } catch (err) {
      toast.error('Failed to update track');
    }
  };

  const handleAdminAudioUpload = async (trackId, file) => {
    setUploadingAudio(trackId);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${BACKEND_URL}/api/tracks/${trackId}/upload-audio`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Audio uploaded');
      await refreshAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload audio');
    } finally {
      setUploadingAudio(null);
    }
  };

  const toggleArrayField = (key, value) => {
    setEditTrack((prev) => {
      const arr = prev[key] || [];
      return { ...prev, [key]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value] };
    });
  };

  // ---- License Agreements ----
  const handleSaveAgreementEdit = async (e) => {
    e.preventDefault();
    setSavingAgreement(true);
    try {
      await axios.patch(`${BACKEND_URL}/api/admin/license-agreements/${editAgreement.id}`, {
        title: editAgreement.title,
        body_text: editAgreement.body_text,
        note: editAgreement.note,
      });
      toast.success('Agreement updated');
      setEditAgreement(null);
      await refreshAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update agreement');
    } finally {
      setSavingAgreement(false);
    }
  };

  const handleCreateNewVersion = async (e) => {
    e.preventDefault();
    setSavingAgreement(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/license-agreements`, {
        license_type: newAgreement.license_type,
        title: newAgreement.title,
        body_text: newAgreement.body_text,
        note: newAgreement.note,
      });
      toast.success('New version created and activated');
      setNewAgreement(null);
      await refreshAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create new version');
    } finally {
      setSavingAgreement(false);
    }
  };

  const handleActivateVersion = async (agreementId) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/license-agreements/${agreementId}/activate`);
      toast.success('Version activated');
      await refreshAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to activate version');
    }
  };

  const handlePreviewAgreement = async (agreementId) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/license-agreements/${agreementId}/preview`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (err) {
      toast.error('Preview failed');
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);

  const formatDate = (s) => (s ? new Date(s).toLocaleDateString() : '—');

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-dark pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  const statCards = [
    { label: 'Total Users', value: stats?.total_users || 0, icon: Users, color: 'text-blue-400' },
    { label: 'Total Artists', value: stats?.total_artists || 0, icon: Music2, color: 'text-emerald-400' },
    { label: 'Total Tracks', value: stats?.total_tracks || 0, icon: Music2, color: 'text-cyan-400' },
    { label: 'Total Revenue', value: formatCurrency(stats?.total_revenue || 0), icon: DollarSign, color: 'text-emerald-400' },
  ];

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'users', label: 'Users & Artists' },
    { id: 'tracks', label: 'Tracks' },
    { id: 'agreements', label: 'License Agreements' },
  ];

  return (
    <div className="min-h-screen bg-gradient-dark pt-24 pb-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="flex items-center gap-4 mb-10">
          <div className="w-12 h-12 bg-emerald-500 rounded-lg flex items-center justify-center">
            <Shield className="w-6 h-6 text-slate-900" />
          </div>
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 block">Administration</span>
            <h1 className="text-3xl font-medium text-white">Admin Panel</h1>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-white/10 pb-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === tab.id ? 'bg-emerald-500 text-slate-900' : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* OVERVIEW */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {statCards.map((stat, i) => (
                <div key={i} className="bg-gradient-card border border-white/5 p-6 rounded-xl" data-testid={`admin-stat-${i}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 bg-white/5 rounded-lg flex items-center justify-center ${stat.color}`}>
                      <stat.icon className="w-5 h-5" />
                    </div>
                  </div>
                  <p className="text-2xl font-semibold text-white">{stat.value}</p>
                  <p className="text-sm text-slate-400">{stat.label}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent purchases */}
              <div className="bg-gradient-card border border-white/5 rounded-xl p-6" data-testid="admin-recent-purchases">
                <div className="flex items-center gap-2 mb-4">
                  <ShoppingBag className="w-5 h-5 text-emerald-400" />
                  <h2 className="text-lg font-semibold text-white">Recent Purchases</h2>
                </div>
                {activity.recent_purchases.length === 0 ? (
                  <p className="text-sm text-slate-500">No completed purchases yet.</p>
                ) : (
                  <div className="space-y-2">
                    {activity.recent_purchases.map((p) => (
                      <div key={p.id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                        <div className="min-w-0">
                          <p className="text-sm text-white truncate">{p.track_title || 'Unknown track'}</p>
                          <p className="text-xs text-slate-500">
                            {p.artist_name || '—'} · {p.license_type}
                          </p>
                        </div>
                        <p className="text-sm font-medium text-emerald-400">{formatCurrency(p.amount)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Recent users */}
              <div className="bg-gradient-card border border-white/5 rounded-xl p-6" data-testid="admin-recent-users">
                <div className="flex items-center gap-2 mb-4">
                  <Activity className="w-5 h-5 text-cyan-400" />
                  <h2 className="text-lg font-semibold text-white">Recent Signups</h2>
                </div>
                <div className="space-y-2">
                  {activity.recent_users.map((u) => (
                    <div key={u.id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                      <div className="min-w-0">
                        <p className="text-sm text-white truncate">{u.name}</p>
                        <p className="text-xs text-slate-500 truncate">{u.email}</p>
                      </div>
                      <span className={`px-2 py-0.5 text-xs rounded-full capitalize ${
                        u.role === 'admin' ? 'bg-emerald-500/10 text-emerald-400'
                        : u.role === 'artist' ? 'bg-cyan-500/10 text-cyan-400'
                        : 'bg-white/10 text-slate-300'
                      }`}>
                        {u.role}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* USERS */}
        {activeTab === 'users' && (
          <div className="bg-gradient-card border border-white/5 rounded-xl overflow-hidden">
            <div className="p-6 border-b border-white/5 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">All Users & Artists</h2>
              <span className="text-sm text-slate-400">{users.length} total</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="users-table">
                <thead>
                  <tr className="border-b border-white/5">
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Name</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Email</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Role</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Joined</th>
                    <th className="text-right p-4 text-sm font-medium text-slate-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`user-row-${u.id}`}>
                      <td className="p-4 text-white">{u.artist_name || u.name}</td>
                      <td className="p-4 text-slate-400">{u.email}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${
                          u.role === 'admin' ? 'bg-emerald-500/10 text-emerald-400'
                          : u.role === 'artist' ? 'bg-cyan-500/10 text-cyan-400'
                          : 'bg-white/10 text-slate-300'
                        }`}>
                          {u.role}
                        </span>
                      </td>
                      <td className="p-4 text-slate-500 text-sm">{formatDate(u.created_at)}</td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => setEditUser({ ...u, password: '' })}
                            className="text-slate-400 hover:text-white"
                            data-testid={`edit-user-${u.id}`}
                            title="Edit user"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          {u.id !== user?.id && u.role !== 'admin' && (
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => handleDeleteUser(u.id, u.name)}
                              className="text-red-400 hover:text-red-300"
                              data-testid={`delete-user-${u.id}`}
                              title="Delete user"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TRACKS */}
        {activeTab === 'tracks' && (
          <div className="bg-gradient-card border border-white/5 rounded-xl overflow-hidden">
            <div className="p-6 border-b border-white/5 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">All Marketplace Tracks</h2>
              <span className="text-sm text-slate-400">{tracks.length} total</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="admin-tracks-table">
                <thead>
                  <tr className="border-b border-white/5">
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Track</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Artist</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Audio</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Sales</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Featured</th>
                    <th className="text-right p-4 text-sm font-medium text-slate-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tracks.map((track) => (
                    <tr key={track.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`track-row-${track.id}`}>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <img src={track.artwork_url} alt={track.title} className="w-10 h-10 object-cover rounded" />
                          <span className="text-white">{track.title}</span>
                        </div>
                      </td>
                      <td className="p-4 text-slate-400">{track.artist_name}</td>
                      <td className="p-4">
                        {track.audio_url ? (
                          <span className="px-2 py-1 bg-emerald-500/10 text-emerald-400 text-xs rounded-full">Uploaded</span>
                        ) : (
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept="audio/*"
                              className="hidden"
                              onChange={(e) => {
                                if (e.target.files?.[0]) handleAdminAudioUpload(track.id, e.target.files[0]);
                              }}
                              data-testid={`admin-upload-audio-${track.id}`}
                            />
                            <Button asChild variant="outline" size="sm"
                              className="border-white/10 text-slate-300 hover:bg-white/5"
                              disabled={uploadingAudio === track.id}
                            >
                              <span>
                                {uploadingAudio === track.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <Upload className="w-4 h-4 mr-1" />
                                    Upload
                                  </>
                                )}
                              </span>
                            </Button>
                          </label>
                        )}
                      </td>
                      <td className="p-4 text-white">{track.total_sales || 0}</td>
                      <td className="p-4">
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => handleToggleFeatured(track)}
                          className={track.is_featured ? 'text-emerald-400' : 'text-slate-400'}
                          data-testid={`feature-btn-${track.id}`}
                        >
                          {track.is_featured ? <StarOff className="w-4 h-4" /> : <Star className="w-4 h-4" />}
                        </Button>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => setEditTrack({
                              ...track,
                              moods: track.moods || [],
                              character: track.character || [],
                            })}
                            className="text-slate-400 hover:text-white"
                            data-testid={`edit-track-${track.id}`}
                            title="Edit track"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => handleDeleteTrack(track.id, track.title)}
                            className="text-red-400 hover:text-red-300"
                            data-testid={`delete-track-${track.id}`}
                            title="Delete track"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* === EDIT USER DIALOG === */}
      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent className="bg-slate-900 border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Edit User</DialogTitle>
          </DialogHeader>
          {editUser && (
            <form onSubmit={handleSaveUser} className="space-y-4 mt-4" data-testid="edit-user-form">
              <div className="space-y-2">
                <Label className="text-slate-300">Name</Label>
                <Input
                  value={editUser.name || ''}
                  onChange={(e) => setEditUser({ ...editUser, name: e.target.value })}
                  className="bg-slate-800 border-white/10 text-white"
                  data-testid="edit-user-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Email</Label>
                <Input
                  type="email"
                  value={editUser.email || ''}
                  onChange={(e) => setEditUser({ ...editUser, email: e.target.value })}
                  className="bg-slate-800 border-white/10 text-white"
                  data-testid="edit-user-email"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Role</Label>
                <Select value={editUser.role} onValueChange={(v) => setEditUser({ ...editUser, role: v })}>
                  <SelectTrigger className="bg-slate-800 border-white/10 text-white" data-testid="edit-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-white/10">
                    {['fan', 'buyer', 'artist', 'admin'].map((r) => (
                      <SelectItem key={r} value={r} className="text-white hover:bg-white/5 capitalize">{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {editUser.role === 'artist' && (
                <>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Artist Name</Label>
                    <Input
                      value={editUser.artist_name || ''}
                      onChange={(e) => setEditUser({ ...editUser, artist_name: e.target.value })}
                      className="bg-slate-800 border-white/10 text-white"
                      data-testid="edit-user-artist-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Bio</Label>
                    <Textarea
                      value={editUser.bio || ''}
                      onChange={(e) => setEditUser({ ...editUser, bio: e.target.value })}
                      className="bg-slate-800 border-white/10 text-white"
                      data-testid="edit-user-bio"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Avatar URL</Label>
                    <Input
                      value={editUser.avatar_url || ''}
                      onChange={(e) => setEditUser({ ...editUser, avatar_url: e.target.value })}
                      placeholder="https://..."
                      className="bg-slate-800 border-white/10 text-white"
                      data-testid="edit-user-avatar"
                    />
                  </div>
                </>
              )}
              <div className="space-y-2">
                <Label className="text-slate-300">Reset Password <span className="text-slate-500 text-xs">(leave blank to keep current)</span></Label>
                <Input
                  type="password"
                  value={editUser.password || ''}
                  onChange={(e) => setEditUser({ ...editUser, password: e.target.value })}
                  placeholder="New password (min 6 chars)"
                  className="bg-slate-800 border-white/10 text-white"
                  data-testid="edit-user-password"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditUser(null)} className="border-white/10 text-slate-300 hover:bg-white/5">
                  Cancel
                </Button>
                <Button type="submit" disabled={savingUser} className="bg-emerald-500 text-slate-900 hover:bg-emerald-400" data-testid="save-user-btn">
                  {savingUser ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* AGREEMENTS */}
      {activeTab === 'agreements' && (
        <div className="space-y-4">
          {['sync', 'commercial', 'nft', 'lyrics', 'exclusive'].map((lt) => {
            const versions = agreements.filter((a) => a.license_type === lt);
            const active = versions.find((v) => v.is_active);
            return (
              <div key={lt} className="bg-gradient-card border border-white/5 rounded-xl overflow-hidden" data-testid={`agreement-card-${lt}`}>
                <div className="p-6 border-b border-white/5 flex items-center justify-between">
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-emerald-400">{lt}</span>
                    <h3 className="text-lg font-semibold text-white mt-0.5">
                      {active ? active.title : 'No agreement yet'}
                    </h3>
                    {active && (
                      <p className="text-xs text-slate-500 mt-1">
                        Active: v{active.version} · {versions.length} version{versions.length !== 1 && 's'} total
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setNewAgreement({
                        license_type: lt,
                        title: active?.title || '',
                        body_text: active?.body_text || '',
                        note: '',
                      })}
                      className="border-white/10 text-slate-300 hover:bg-white/5"
                      data-testid={`new-version-${lt}`}
                    >
                      New Version
                    </Button>
                    {active && (
                      <Button
                        size="sm"
                        onClick={() => setEditAgreement({ ...active })}
                        className="bg-emerald-500 text-slate-900 hover:bg-emerald-400 font-medium"
                        data-testid={`edit-active-${lt}`}
                      >
                        <Pencil className="w-3.5 h-3.5 mr-1" />
                        Edit Active
                      </Button>
                    )}
                  </div>
                </div>

                <div className="p-6 space-y-3">
                  {versions.length === 0 ? (
                    <p className="text-sm text-slate-500">No versions yet — click "New Version" to create one.</p>
                  ) : (
                    versions.map((v) => (
                      <div
                        key={v.id}
                        className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                          v.is_active
                            ? 'border-emerald-500/40 bg-emerald-500/5'
                            : 'border-white/10 bg-slate-800/40 hover:bg-slate-800/60'
                        }`}
                        data-testid={`agreement-version-${lt}-${v.version}`}
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-white">
                            v{v.version}
                            {v.is_active && <span className="ml-2 px-2 py-0.5 text-[10px] font-bold uppercase bg-emerald-500 text-slate-900 rounded-full">Active</span>}
                          </p>
                          <p className="text-xs text-slate-400 truncate">{v.note || v.title}</p>
                          <p className="text-xs text-slate-600 mt-0.5">{formatDate(v.created_at)}</p>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handlePreviewAgreement(v.id)}
                            className="text-slate-400 hover:text-white"
                            title="Preview PDF"
                            data-testid={`preview-${lt}-${v.version}`}
                          >
                            Preview PDF
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditAgreement({ ...v })}
                            className="text-slate-400 hover:text-white"
                            title="Edit this version"
                            data-testid={`edit-${lt}-${v.version}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          {!v.is_active && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleActivateVersion(v.id)}
                              className="text-emerald-400 hover:text-emerald-300"
                              title="Make this version active"
                              data-testid={`activate-${lt}-${v.version}`}
                            >
                              Activate
                            </Button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
          <div className="text-xs text-slate-500 mt-4 px-2">
            <strong>Available placeholders:</strong>{' '}
            <code className="text-emerald-400">{`{{buyer_name}}`}</code>,{' '}
            <code className="text-emerald-400">{`{{buyer_email}}`}</code>,{' '}
            <code className="text-emerald-400">{`{{track_title}}`}</code>,{' '}
            <code className="text-emerald-400">{`{{artist_name}}`}</code>,{' '}
            <code className="text-emerald-400">{`{{artist_email}}`}</code>,{' '}
            <code className="text-emerald-400">{`{{amount}}`}</code>,{' '}
            <code className="text-emerald-400">{`{{purchase_date}}`}</code>,{' '}
            <code className="text-emerald-400">{`{{license_type}}`}</code>,{' '}
            <code className="text-emerald-400">{`{{platform_name}}`}</code>
          </div>
        </div>
      )}

      {/* === EDIT TRACK DIALOG === */}
      <Dialog open={!!editTrack} onOpenChange={(o) => !o && setEditTrack(null)}>
        <DialogContent className="bg-slate-900 border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Track</DialogTitle>
          </DialogHeader>
          {editTrack && (
            <form onSubmit={handleSaveTrack} className="space-y-4 mt-4" data-testid="edit-track-form">
              <div className="space-y-2">
                <Label className="text-slate-300">Title</Label>
                <Input
                  value={editTrack.title || ''}
                  onChange={(e) => setEditTrack({ ...editTrack, title: e.target.value })}
                  className="bg-slate-800 border-white/10 text-white"
                  data-testid="edit-track-title"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Description</Label>
                <Textarea
                  value={editTrack.description || ''}
                  onChange={(e) => setEditTrack({ ...editTrack, description: e.target.value })}
                  className="bg-slate-800 border-white/10 text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Genre</Label>
                  <Input
                    value={editTrack.genre || ''}
                    onChange={(e) => setEditTrack({ ...editTrack, genre: e.target.value })}
                    className="bg-slate-800 border-white/10 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">BPM</Label>
                  <Input
                    type="number"
                    value={editTrack.bpm || 0}
                    onChange={(e) => setEditTrack({ ...editTrack, bpm: parseInt(e.target.value) || 0 })}
                    className="bg-slate-800 border-white/10 text-white"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Artwork URL</Label>
                <Input
                  value={editTrack.artwork_url || ''}
                  onChange={(e) => setEditTrack({ ...editTrack, artwork_url: e.target.value })}
                  className="bg-slate-800 border-white/10 text-white"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label className="text-slate-300">Sync $</Label>
                  <Input type="number" value={editTrack.price_sync || 0}
                    onChange={(e) => setEditTrack({ ...editTrack, price_sync: parseFloat(e.target.value) || 0 })}
                    className="bg-slate-800 border-white/10 text-white" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Commercial $</Label>
                  <Input type="number" value={editTrack.price_commercial || 0}
                    onChange={(e) => setEditTrack({ ...editTrack, price_commercial: parseFloat(e.target.value) || 0 })}
                    className="bg-slate-800 border-white/10 text-white" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">NFT $</Label>
                  <Input type="number" value={editTrack.price_nft || 0}
                    onChange={(e) => setEditTrack({ ...editTrack, price_nft: parseFloat(e.target.value) || 0 })}
                    className="bg-slate-800 border-white/10 text-white" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label className="text-slate-300">Content Rating</Label>
                  <Select value={editTrack.content_rating || 'General'} onValueChange={(v) => setEditTrack({ ...editTrack, content_rating: v })}>
                    <SelectTrigger className="bg-slate-800 border-white/10 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-white/10">
                      {vocab.content_rating.map((o) => <SelectItem key={o} value={o} className="text-white hover:bg-white/5">{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Voice</Label>
                  <Select value={editTrack.voice || 'Instrumental'} onValueChange={(v) => setEditTrack({ ...editTrack, voice: v })}>
                    <SelectTrigger className="bg-slate-800 border-white/10 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-white/10">
                      {vocab.voice.map((o) => <SelectItem key={o} value={o} className="text-white hover:bg-white/5">{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Language</Label>
                  <Select value={editTrack.language || 'English'} onValueChange={(v) => setEditTrack({ ...editTrack, language: v })}>
                    <SelectTrigger className="bg-slate-800 border-white/10 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-white/10">
                      {vocab.language.map((o) => <SelectItem key={o} value={o} className="text-white hover:bg-white/5">{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Moods</Label>
                <div className="flex flex-wrap gap-2">
                  {vocab.moods.map((m) => {
                    const active = (editTrack.moods || []).includes(m);
                    return (
                      <button type="button" key={m} onClick={() => toggleArrayField('moods', m)}
                        className={`px-3 py-1 text-xs rounded-full border transition-colors ${active ? 'bg-emerald-500 border-emerald-500 text-slate-900 font-medium' : 'bg-slate-800 border-white/10 text-slate-300 hover:bg-slate-700'}`}>
                        {m}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Character</Label>
                <div className="flex flex-wrap gap-2">
                  {vocab.character.map((c) => {
                    const active = (editTrack.character || []).includes(c);
                    return (
                      <button type="button" key={c} onClick={() => toggleArrayField('character', c)}
                        className={`px-3 py-1 text-xs rounded-full border transition-colors ${active ? 'bg-emerald-500 border-emerald-500 text-slate-900 font-medium' : 'bg-slate-800 border-white/10 text-slate-300 hover:bg-slate-700'}`}>
                        {c}
                      </button>
                    );
                  })}
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditTrack(null)} className="border-white/10 text-slate-300 hover:bg-white/5">
                  Cancel
                </Button>
                <Button type="submit" disabled={savingTrack} className="bg-emerald-500 text-slate-900 hover:bg-emerald-400" data-testid="save-track-btn">
                  {savingTrack ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* === EDIT LICENSE AGREEMENT DIALOG === */}
      <Dialog open={!!editAgreement} onOpenChange={(o) => !o && setEditAgreement(null)}>
        <DialogContent className="bg-slate-900 border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">
              Edit Agreement — {editAgreement?.license_type} v{editAgreement?.version}
              {editAgreement?.is_active && <span className="ml-2 px-2 py-0.5 text-[10px] font-bold bg-emerald-500 text-slate-900 rounded-full uppercase">Active</span>}
            </DialogTitle>
          </DialogHeader>
          {editAgreement && (
            <form onSubmit={handleSaveAgreementEdit} className="space-y-4 mt-3" data-testid="edit-agreement-form">
              <div className="space-y-2">
                <Label className="text-slate-300">Title</Label>
                <Input
                  value={editAgreement.title || ''}
                  onChange={(e) => setEditAgreement({ ...editAgreement, title: e.target.value })}
                  className="bg-slate-800 border-white/10 text-white"
                  data-testid="edit-agreement-title"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Note</Label>
                <Input
                  value={editAgreement.note || ''}
                  onChange={(e) => setEditAgreement({ ...editAgreement, note: e.target.value })}
                  placeholder="What changed in this version?"
                  className="bg-slate-800 border-white/10 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">
                  Body Text <span className="text-slate-500 text-xs">(Markdown — use {`{{placeholders}}`} for dynamic values)</span>
                </Label>
                <Textarea
                  value={editAgreement.body_text || ''}
                  onChange={(e) => setEditAgreement({ ...editAgreement, body_text: e.target.value })}
                  className="bg-slate-800 border-white/10 text-white font-mono text-xs min-h-[300px]"
                  data-testid="edit-agreement-body"
                />
              </div>
              <p className="text-xs text-amber-400/80">
                Note: editing this version updates it in place. Past purchases that used this version will reflect the changes. Use "New Version" to keep a stable historical record.
              </p>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditAgreement(null)} className="border-white/10 text-slate-300 hover:bg-white/5">
                  Cancel
                </Button>
                <Button type="submit" disabled={savingAgreement} className="bg-emerald-500 text-slate-900 hover:bg-emerald-400" data-testid="save-agreement-btn">
                  {savingAgreement ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* === NEW VERSION DIALOG === */}
      <Dialog open={!!newAgreement} onOpenChange={(o) => !o && setNewAgreement(null)}>
        <DialogContent className="bg-slate-900 border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">
              New Version — {newAgreement?.license_type}
            </DialogTitle>
          </DialogHeader>
          {newAgreement && (
            <form onSubmit={handleCreateNewVersion} className="space-y-4 mt-3" data-testid="new-agreement-form">
              <p className="text-sm text-slate-400">
                Creating a new version will deactivate the previous one. Past purchases keep using the version they were made under.
              </p>
              <div className="space-y-2">
                <Label className="text-slate-300">Title</Label>
                <Input
                  required
                  value={newAgreement.title}
                  onChange={(e) => setNewAgreement({ ...newAgreement, title: e.target.value })}
                  className="bg-slate-800 border-white/10 text-white"
                  data-testid="new-agreement-title"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Note <span className="text-slate-500 text-xs">(what changed)</span></Label>
                <Input
                  value={newAgreement.note}
                  onChange={(e) => setNewAgreement({ ...newAgreement, note: e.target.value })}
                  placeholder="e.g. Updated credit clause; expanded sync usage scope"
                  className="bg-slate-800 border-white/10 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">
                  Body Text <span className="text-slate-500 text-xs">(Markdown + {`{{placeholders}}`})</span>
                </Label>
                <Textarea
                  required
                  value={newAgreement.body_text}
                  onChange={(e) => setNewAgreement({ ...newAgreement, body_text: e.target.value })}
                  className="bg-slate-800 border-white/10 text-white font-mono text-xs min-h-[300px]"
                  data-testid="new-agreement-body"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setNewAgreement(null)} className="border-white/10 text-slate-300 hover:bg-white/5">
                  Cancel
                </Button>
                <Button type="submit" disabled={savingAgreement} className="bg-emerald-500 text-slate-900 hover:bg-emerald-400" data-testid="create-agreement-btn">
                  {savingAgreement ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create & Activate'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
